import useBreakpoints from '@/hooks/useBreakpoints';
import clsx from 'clsx';
import React, { ComponentProps, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { PopoverButton, PopoverPanel, PopoverWrapper } from 'ui/ds/Popover';
import { PopperJS, usePopper } from 'ui/ds/Popover/Popover';
import { DateRange } from 'ui/icons';
import FormInput from '../FormInput/FormInput';

interface IProps {
  placeholder?: string;
  value?: string;
  inputProps?: ComponentProps<typeof FormInput>;
  popperOptions?: Partial<
    PopperJS.Options & {
      createPopper?: typeof PopperJS.createPopper;
    }
  >;
  dayPickerProps?: Partial<ComponentProps<typeof DayPicker>>;
  onDisabledDateClick?: (date: string) => void;
  disabled?: boolean;
  disableFutureDates?: boolean;
  isInsideModal?: boolean;
}

const DatePicker = ({
  inputProps,
  placeholder,
  value,
  popperOptions,
  dayPickerProps,
  onDisabledDateClick,
  disabled,
  disableFutureDates = false,
  isInsideModal = false,
}: IProps) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    ...popperOptions,
  });

  const { isMobile } = useBreakpoints();

  const isOverlay = isMobile || isInsideModal;

  const { classNames: dayPickerClassNames, ...restDayPickerProps } =
    dayPickerProps ?? {};

  // Separate the RHF `ref` onto a hidden input so that `form.reset()` (which
  // directly mutates the DOM via the ref, bypassing React's `value` prop) never
  // writes the raw ISO string to the visible display input.
  const {
    value: _consumerValue,
    defaultValue: _consumerDefaultValue,
    ref: registerRef,
    ...restDisplayProps
  } = inputProps?.inputProps ?? {};

  const handlePointerDown = (event: React.PointerEvent) => {
    const dayElement = (event?.target as HTMLElement)?.closest?.('td');
    const isDisabled = dayElement?.dataset?.disabled === 'true';
    if (isDisabled) {
      event.preventDefault();
      const selectedDate = dayElement?.dataset?.date ?? '';
      onDisabledDateClick?.(selectedDate);
    }
  };

  return (
    <>
      {registerRef && (
        <input
          type="text"
          style={{ display: 'none', position: 'absolute' }}
          ref={registerRef as React.Ref<HTMLInputElement>}
          name={restDisplayProps.name}
          readOnly
        />
      )}
    <FormInput
      {...inputProps}
      inputProps={{
        placeholder,
        readOnly: true,
        onFocus: (event) => {
          if (event.relatedTarget === referenceElement) {
            return; // Skip if focus came from the PopoverButton
          }
          if (referenceElement) {
            referenceElement?.click();
          }
        },
        onClick: (event) => {
          if (event.relatedTarget === referenceElement) {
            return; // Skip if focus came from the PopoverButton
          }
          if (referenceElement) {
            referenceElement?.click();
          }
        },
        trailingIcon: (
          <PopoverWrapper className="relative">
            <PopoverButton
              ref={(elem) => setReferenceElement(elem)}
              className={clsx({
                'flex items-center': true,
                '!cursor-not-allowed': disabled,
              })}
              disabled={disabled}
            >
              <DateRange />
            </PopoverButton>
            <PopoverPanel
              ref={setPopperElement}
              {...(isOverlay ? {} : attributes.popper)}
              className={clsx({
                'relative z-40': true,
                'flex flex-col w-screen fixed inset-0': isOverlay,
              })}
            >
              {({ close }) => {
                return (
                  <>
                    {isOverlay ? (
                      <div
                        onClick={() => close()}
                        className="fixed bg-mobile-gray-700 bg-opacity-50 inset-0 -z-1 h-screen w-screen"
                      />
                    ) : null}
                    <div
                      style={{
                        ...(isOverlay ? { position: 'fixed' } : styles.popper),
                      }}
                      className={clsx({
                        'p-5 bg-white shadow-card flex flex-col justify-center text-left rounded-lg h-fit md:w-[328px]':
                          true,
                        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2':
                          isOverlay,
                      })}
                      onPointerDown={handlePointerDown}
                    >
                      <DayPicker
                        disabled={
                          disableFutureDates ? { after: new Date() } : undefined
                        } // Disable future dates
                        captionLayout="dropdown-years"
                        mode="single"
                        fixedWeeks
                        classNames={{
                          chevron: 'text-primary-500 fill-current',
                          selected: 'bg-primary-500 text-white rounded-full',
                          weekday:
                            'text-mobile-gray-400 font-medium text-sm uppercase text-center cursor-default w-10 h-10 flex items-center justify-center',
                          day: 'w-10 h-10 text-center border border-transparent rounded-full border-box text-xl text-mobile-text-main leading-[120%] font-normal items-center justify-center flex',
                          day_button:
                            'text-center cursor-pointer border border-transparent rounded-full border-box text-sm w-full h-full',
                          today: 'border border-primary-500 text-primary-500',
                          month_caption:
                            'cursor-default text-mobile-text-main text-xl leading-[120%] font-medium h-9 px-2',
                          nav: 'h-11 w-14 right-1.5 -top-2.5 absolute flex items-center justify-between',
                          week: 'flex justify-between w-[280px] py-[2px] gap-0',
                          weeks: 'w-[320px]',
                          weekdays: 'flex justify-between w-[280px]',
                          root: 'mx-auto max-w-full',
                          disabled: '!text-mobile-gray-300',
                          dropdown_root: 'ml-2',
                          ...dayPickerClassNames,
                        }}
                        {...restDayPickerProps}
                        // @ts-ignore
                        onSelect={(...args) => {
                          // @ts-ignore
                          dayPickerProps?.onSelect?.(...args);
                          close();
                        }}
                      />
                    </div>
                  </>
                );
              }}
            </PopoverPanel>
          </PopoverWrapper>
        ),
        className: clsx({
          'cursor-pointer': true,
          '!cursor-not-allowed': disabled,
        }),
        disabled,
        ...restDisplayProps,
        value,
      }}
    />
    </>
  );
};

export default DatePicker;
